import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, Sparkles, Trash2, PencilLine, Save, Plus, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
    { key: "pen_assessment", labelKey: "forms.category_pen", label: "Pen Assessments" },
    { key: "cleaning", labelKey: "forms.category_cleaning", label: "Cleaning Forms" },
    { key: "custom", labelKey: "forms.category_custom", label: "Custom" },
];

function scoreColor(pct) {
    if (pct >= 85) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
    if (pct >= 60) return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
    return "bg-rose-500/15 text-rose-700 dark:text-rose-300";
}

// ---- Dynamic field renderer ----
function FieldInput({ field, value, onChange }) {
    const common = { "data-testid": `form-field-${field.key}` };
    if (field.type === "checkbox") {
        return (
            <label className="flex items-center gap-3 py-1">
                <Checkbox
                    checked={value === true}
                    onCheckedChange={(v) => onChange(v === true)}
                    {...common}
                />
                <span className="text-sm">{field.label}</span>
                {field.score_if_yes ? (
                    <Badge variant="outline" className="text-[10px]">
                        {field.score_if_yes > 0 ? `+${field.score_if_yes}` : field.score_if_yes} {i18n.t("batch4.4abdfc6d463e")}
                    </Badge>
                ) : null}
            </label>
        );
    }
    if (field.type === "scale") {
        const v = Number(value) || 0;
        return (
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm">{field.label}</span>
                    <Badge variant="outline" className="text-[10px]">{v}/5</Badge>
                </div>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Button
                            key={n}
                            type="button"
                            size="sm"
                            variant={v === n ? "default" : "outline"}
                            className="w-10 h-10 p-0"
                            onClick={() => onChange(n)}
                            data-testid={`scale-${field.key}-${n}`}
                        >
                            {n}
                        </Button>
                    ))}
                </div>
            </div>
        );
    }
    if (field.type === "select") {
        return (
            <div>
                <Label className="text-sm">{field.label}</Label>
                <Select value={value || ""} onValueChange={onChange}>
                    <SelectTrigger className="mt-1 h-11" {...common}>
                        <SelectValue placeholder={i18n.t("batch4.c3b69e661eec")} />
                    </SelectTrigger>
                    <SelectContent>
                        {(field.options || []).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label} {opt.score ? `(+${opt.score})` : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }
    if (field.type === "textarea") {
        return (
            <div>
                <Label className="text-sm">{field.label}</Label>
                <Textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder=""
                    className="mt-1"
                    rows={3}
                    {...common}
                />
            </div>
        );
    }
    if (field.type === "number") {
        return (
            <div>
                <Label className="text-sm">
                    {field.label}
                    {(field.min !== undefined || field.max !== undefined) && (
                        <span className="text-[10px] text-muted-foreground ml-1">
                            ({field.min !== undefined ? `min ${field.min}` : ""}
                            {field.min !== undefined && field.max !== undefined ? ", " : ""}
                            {field.max !== undefined ? `max ${field.max}` : ""})
                        </span>
                    )}
                </Label>
                <Input
                    type="number"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                    className="mt-1 h-11"
                    {...common}
                />
            </div>
        );
    }
    // default text
    return (
        <div>
            <Label className="text-sm">{field.label}</Label>
            <Input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 h-11"
                {...common}
            />
        </div>
    );
}

// ---- Fill Form Dialog ----
function FillFormDialog({ template, open, onClose, onSubmitted }) {
    const [answers, setAnswers] = useState({});
    const [location, setLocation] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open) {
            setAnswers({});
            setLocation("");
            setNotes("");
            setResult(null);
        }
    }, [open, template?.id]);

    const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

    const submit = async () => {
        try {
            setSubmitting(true);
            const r = await api.post("/forms/submissions", {
                template_id: template.id,
                location,
                answers,
                notes,
            });
            setResult(r.data);
            onSubmitted?.();
            toast.success(`Submitted · Score ${r.data.percent}%`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!template) return null;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fill-form-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5" />
                        {template.title}
                    </DialogTitle>
                    {template.description && (
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                    )}
                </DialogHeader>

                {result ? (
                    <div className="space-y-3 py-4">
                        <div className={`p-6 rounded-xl ${scoreColor(result.percent)}`}>
                            <div className="text-sm">{i18n.t("batch4.489f4877244a")}</div>
                            <div className="text-4xl font-bold" data-testid="submission-percent">
                                {result.percent}%
                            </div>
                            <div className="text-sm">
                                {result.score} / {result.max_score} {i18n.t("batch4.819f41f05837")}
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {i18n.t("batch4.c3e9bd0582df")} {new Date(result.submitted_at).toLocaleString()}
                        </div>
                        <div className="max-h-56 overflow-y-auto border rounded-md p-3 text-xs space-y-1">
                            {(result.breakdown || []).map((b, i) => (
                                <div key={i} className="flex justify-between gap-3">
                                    <span className="truncate">{b.label}</span>
                                    <span className="font-mono whitespace-nowrap">
                                        {b.earned} / {b.possible}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <Button onClick={onClose} className="w-full h-11" data-testid="submission-close-btn">
                            {i18n.t("batch4.bbfa773e5a63")}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-sm">{i18n.t("batch4.d3197241f50e")}</Label>
                            <Input
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                placeholder={i18n.t("batch4.3988dcb104c9")}
                                className="mt-1 h-11"
                                data-testid="form-location"
                            />
                        </div>
                        {(template.fields || []).map((f) => (
                            <FieldInput
                                key={f.key}
                                field={f}
                                value={answers[f.key]}
                                onChange={(v) => setAnswer(f.key, v)}
                            />
                        ))}
                        <div>
                            <Label className="text-sm">{i18n.t("batch4.a6886107051f")}</Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="mt-1"
                                rows={2}
                                data-testid="form-additional-notes"
                            />
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={onClose} data-testid="form-cancel-btn">
                                {i18n.t("batch4.77dfd2135f4d")}
                            </Button>
                            <Button onClick={submit} disabled={submitting} data-testid="form-submit-btn">
                                <Save className="w-4 h-4 mr-2" />
                                {submitting ? "Submitting…" : "Submit & Score"}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ---- Template Editor (Owner-only) ----
function TemplateEditor({ template, open, onClose, onSaved }) {
    const [draft, setDraft] = useState(null);

    useEffect(() => {
        if (open && template) {
            setDraft(JSON.parse(JSON.stringify(template)));
        }
    }, [open, template?.id]);

    if (!draft) return null;

    const updateField = (idx, patch) =>
        setDraft((d) => {
            const fields = [...(d.fields || [])];
            fields[idx] = { ...fields[idx], ...patch };
            return { ...d, fields };
        });
    const removeField = (idx) =>
        setDraft((d) => ({ ...d, fields: (d.fields || []).filter((_, i) => i !== idx) }));
    const addField = () =>
        setDraft((d) => ({
            ...d,
            fields: [...(d.fields || []), { key: `field_${(d.fields || []).length + 1}`, label: "New field", type: "text" }],
        }));

    const save = async () => {
        try {
            const payload = {
                code: draft.code,
                title: draft.title,
                description: draft.description || "",
                category: draft.category,
                fields: draft.fields || [],
                active: draft.active !== false,
                sort_order: draft.sort_order || 0,
            };
            if (draft.id) {
                await api.put(`/forms/templates/${draft.id}`, payload);
            } else {
                await api.post("/forms/templates", payload);
            }
            toast.success("Template saved");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-editor">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PencilLine className="w-5 h-5" />
                        {draft.id ? "Edit Template" : "New Template"}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("batch4.768e0c1c6957")}</Label>
                            <Input
                                value={draft.title || ""}
                                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                className="mt-1 h-10"
                                data-testid="tpl-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("batch4.a3c686e711e4")}</Label>
                            <Select
                                value={draft.category || "custom"}
                                onValueChange={(v) => setDraft({ ...draft, category: v })}
                            >
                                <SelectTrigger className="mt-1 h-10" data-testid="tpl-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c.key} value={c.key}>
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("batch4.55f8ebc805e6")}</Label>
                        <Textarea
                            value={draft.description || ""}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            className="mt-1"
                            rows={2}
                        />
                    </div>

                    <div className="border rounded-md p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="font-semibold text-sm">{i18n.t("batch4.f2cc30b13d8e")}{(draft.fields || []).length})</div>
                            <Button size="sm" onClick={addField} data-testid="add-field-btn">
                                <Plus className="w-4 h-4 mr-1" /> {i18n.t("batch4.55f9626c6c05")}
                            </Button>
                        </div>
                        {(draft.fields || []).map((f, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-end border-b pb-2">
                                <div className="col-span-3">
                                    <Label className="text-[10px]">{i18n.t("batch4.c67dd20ee842")}</Label>
                                    <Input
                                        value={f.key || ""}
                                        onChange={(e) => updateField(idx, { key: e.target.value })}
                                        className="h-9 text-xs"
                                    />
                                </div>
                                <div className="col-span-4">
                                    <Label className="text-[10px]">{i18n.t("batch4.74341e3c271d")}</Label>
                                    <Input
                                        value={f.label || ""}
                                        onChange={(e) => updateField(idx, { label: e.target.value })}
                                        className="h-9 text-xs"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-[10px]">{i18n.t("batch4.3deb74565196")}</Label>
                                    <Select value={f.type} onValueChange={(v) => updateField(idx, { type: v })}>
                                        <SelectTrigger className="h-9 text-xs">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {["text", "number", "checkbox", "scale", "select", "textarea"].map((t) => (
                                                <SelectItem key={t} value={t}>
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-[10px]">{i18n.t("batch4.b1786d43e65e")}</Label>
                                    <Input
                                        type="number"
                                        value={f.weight ?? f.score_if_yes ?? ""}
                                        onChange={(e) => {
                                            const v = e.target.value === "" ? undefined : Number(e.target.value);
                                            if (f.type === "checkbox") updateField(idx, { score_if_yes: v });
                                            else updateField(idx, { weight: v });
                                        }}
                                        className="h-9 text-xs"
                                    />
                                </div>
                                <div className="col-span-1">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => removeField(idx)}
                                        data-testid={`remove-field-${idx}`}
                                    >
                                        <Trash2 className="w-4 h-4 text-rose-500" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>{i18n.t("batch4.77dfd2135f4d")}</Button>
                    <Button onClick={save} data-testid="tpl-save-btn">
                        <Save className="w-4 h-4 mr-2" /> {i18n.t("batch4.efc007a393f6")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---- Main Page ----
export default function FormsPage() {
    const { t } = useTranslation();
    const { isOwner } = useAuth();
    const [tab, setTab] = useState("pen_assessment");
    const [templates, setTemplates] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fillFor, setFillFor] = useState(null);
    const [editFor, setEditFor] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const [tpl, sub] = await Promise.all([
                api.get("/forms/templates"),
                api.get("/forms/submissions?limit=50"),
            ]);
            setTemplates(tpl.data || []);
            setSubmissions(sub.data || []);
        } catch (e) {
            toast.error("Failed to load forms");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(
        () => templates.filter((tpl) => tpl.category === tab && tpl.active !== false),
        [templates, tab]
    );

    const deleteTemplate = async (tpl) => {
        if (!confirm(`Delete/Disable "${tpl.title}"?`)) return;
        try {
            await api.delete(`/forms/templates/${tpl.id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const createNew = () => {
        setEditFor({
            id: null,
            title: "",
            description: "",
            category: tab,
            fields: [],
            active: true,
        });
    };

    return (
        <div className="space-y-6" data-testid="forms-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3">
                        <ClipboardCheck className="w-8 h-8 text-primary" />
                        {t("forms.title", "Assessments & Cleaning Forms")}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {t("forms.subtitle", "25 built-in templates (11 pen assessments + 14 cleaning). Fully editable.")}
                    </p>
                </div>
                {isOwner() && (
                    <Button onClick={createNew} data-testid="new-template-btn">
                        <Plus className="w-4 h-4 mr-2" /> {t("forms.new_template", "New Template")}
                    </Button>
                )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    {CATEGORIES.map((c) => (
                        <TabsTrigger key={c.key} value={c.key} data-testid={`tab-${c.key}`}>
                            {t(c.labelKey, c.label)}
                            <Badge variant="secondary" className="ml-2 text-[10px]">
                                {templates.filter((t) => t.category === c.key && t.active !== false).length}
                            </Badge>
                        </TabsTrigger>
                    ))}
                    <TabsTrigger value="_submissions" data-testid="tab-submissions">
                        {t("forms.submissions", "Submissions")}
                        <Badge variant="secondary" className="ml-2 text-[10px]">{submissions.length}</Badge>
                    </TabsTrigger>
                </TabsList>

                {CATEGORIES.map((c) => (
                    <TabsContent key={c.key} value={c.key} className="mt-4">
                        {loading ? (
                            <div className="text-muted-foreground">{t("common.loading", "Loading…")}</div>
                        ) : filtered.length === 0 ? (
                            <Card className="p-8 text-center text-muted-foreground">
                                {i18n.t("batch4.da287a6991f2")}
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filtered.map((tpl) => (
                                    <Card key={tpl.id} className="p-5 flex flex-col" data-testid={`template-card-${tpl.code}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-lg leading-tight">{tpl.title}</div>
                                                <div className="text-xs text-muted-foreground mt-1">{tpl.description}</div>
                                            </div>
                                            {tpl.is_default && (
                                                <Badge variant="outline" className="text-[9px] whitespace-nowrap">
                                                    <Sparkles className="w-3 h-3 mr-0.5" />
                                                    {i18n.t("batch4.7505d64a54e0")}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-3">
                                            {(tpl.fields || []).length} {i18n.t("batch4.2da0b68df884")}{(tpl.fields || []).length !== 1 ? "s" : ""}
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() => setFillFor(tpl)}
                                                data-testid={`fill-${tpl.code}`}
                                            >
                                                <ClipboardCheck className="w-4 h-4 mr-1" />
                                                {i18n.t("batch4.7adb6736cf2c")}
                                            </Button>
                                            {isOwner() && (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setEditFor(tpl)}
                                                        data-testid={`edit-${tpl.code}`}
                                                    >
                                                        <PencilLine className="w-4 h-4 mr-1" />
                                                        {i18n.t("batch4.5301648dcf6b")}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => deleteTemplate(tpl)}
                                                        data-testid={`delete-${tpl.code}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-rose-500" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                ))}

                <TabsContent value="_submissions" className="mt-4">
                    {submissions.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">
                            {i18n.t("batch4.e8f43bc66219")}
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {submissions.map((s) => (
                                <Card key={s.id} className="p-4" data-testid={`submission-${s.id}`}>
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{s.template_title}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {s.location || "—"} {i18n.t("batch4.bb37dabe3e19")} {s.submitted_by_name || "?"}
                                            </div>
                                        </div>
                                        <div className={`px-3 py-1 rounded-md font-mono text-xs ${scoreColor(s.percent)}`}>
                                            {s.percent}%
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-2">
                                        {new Date(s.submitted_at).toLocaleString()}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <FillFormDialog
                template={fillFor}
                open={!!fillFor}
                onClose={() => setFillFor(null)}
                onSubmitted={load}
            />
            <TemplateEditor
                template={editFor}
                open={!!editFor}
                onClose={() => setEditFor(null)}
                onSaved={load}
            />
        </div>
    );
}
